<div class="supplier-container">
  <mat-card>
    <mat-card-content>
      <ng-container *ngIf="loadErrorMessage$ | async as loadErrorMessage; else testsContent">
        <div class="heat-trace-error" *ngIf="loadErrorMessage">{{ loadErrorMessage }}</div>
      </ng-container>

      <ng-template #testsContent>
        <div class="heat-trace-overview" [class.heat-trace-overview--loading]="testsLoading$ | async">
          <div class="heat-trace-nav">
            <div class="heat-trace-nav__toolbar">
              <div class="heat-trace-nav__search">
                <label class="heat-trace-visually-hidden" for="heat-trace-search">Search tests</label>
                <div class="heat-trace-nav__search-box">
                  <input
                    id="heat-trace-search"
                    class="heat-trace-nav__search-input"
                    type="text"
                    placeholder="Type tag number or name"
                    [(ngModel)]="searchQuery"
                    (keydown.enter)="onSearchSubmit()"
                    (blur)="onSearchSubmit()"
                  />
                  <button type="button" class="heat-trace-nav__search-button" (click)="onSearchSubmit(true)">Search</button>
                </div>
              </div>

              <div class="heat-trace-nav__status-filters">
                <button
                  type="button"
                  class="heat-trace-nav__status-button"
                  [class.active]="!areSearchStatusFiltersDisabled && selectedStatusFilter === 'ALL'"
                  [disabled]="areSearchStatusFiltersDisabled"
                  (click)="setStatusFilter('ALL')"
                >
                  All
                </button>
                <button
                  type="button"
                  class="heat-trace-nav__status-button"
                  *ngFor="let status of availableStatusFilters"
                  [class.active]="!areSearchStatusFiltersDisabled && selectedStatusFilter === status"
                  [disabled]="areSearchStatusFiltersDisabled"
                  (click)="setStatusFilter(status)"
                >
                  {{ status }}
                </button>
              </div>

              <div class="heat-trace-nav__sort-controls" *ngIf="isSearchMode">
                <label class="heat-trace-visually-hidden" for="heat-trace-order-by">Order by</label>
                <select
                  id="heat-trace-order-by"
                  class="heat-trace-nav__sort-select"
                  [ngModel]="orderBy"
                  [disabled]="areSearchSortControlsDisabled"
                  (ngModelChange)="onOrderByChange($event)"
                >
                  <option *ngFor="let option of orderByOptions" [ngValue]="option">{{ getOrderByLabel(option) }}</option>
                </select>

                <label class="heat-trace-visually-hidden" for="heat-trace-direction">Direction</label>
                <select
                  id="heat-trace-direction"
                  class="heat-trace-nav__sort-select"
                  [ngModel]="direction"
                  [disabled]="areSearchSortControlsDisabled"
                  (ngModelChange)="onDirectionChange($event)"
                >
                  <option *ngFor="let option of directionOptions" [ngValue]="option">{{ option }}</option>
                </select>
              </div>

              <div class="heat-trace-nav__date">
                <label class="heat-trace-visually-hidden" for="heat-trace-start-date">Jump to date</label>
                <div class="heat-trace-nav__date-controls">
                  <button
                    type="button"
                    class="heat-trace-nav__home-button"
                    (click)="goToCurrentWeekPage()"
                    [disabled]="isTodayButtonDisabled"
                    aria-label="Go to current week"
                  >
                    <mat-icon>today</mat-icon>
                  </button>
                  <p-datepicker
                    class="heat-trace-nav__picker"
                    [(ngModel)]="selectedStartDate"
                    (ngModelChange)="onStartDateChange($event)"
                    [showIcon]="true"
                    [showButtonBar]="true"
                    [showOnFocus]="false"
                    dateFormat="yy-mm-dd"
                    inputId="heat-trace-start-date"
                    placeholder="Jump to date"
                  ></p-datepicker>
                </div>
              </div>
            </div>

            <div class="heat-trace-search-results" *ngIf="isSearchMode">
              <h1 *ngIf="!awaitingSearchResponse" class="heat-trace-search-results__heading">Search results</h1>

              <div
                class="heat-trace-search-result"
                *ngFor="let result of filteredSearchResults"
                (click)="openSearchResult(result.test.tagNumber)"
                role="button"
                tabindex="0"
                (keydown.enter)="openSearchResult(result.test.tagNumber)"
              >
                <div class="heat-trace-search-result__left" [style.border-left-color]="getSearchResultStatusColor(result)">
                  <div class="heat-trace-search-result__date">{{ result.handoverDate || '-' }}</div>
                  <div class="heat-trace-search-result__title">{{ result.displayTitle }}</div>
                </div>

                <div class="heat-trace-search-result__right">
                  <app-status-circle [status]="result.statusName" [useSmall]="true" />
                  <button
                    type="button"
                    class="heat-trace-search-result__up"
                    (click)="goToOverviewForResult($event, result)"
                    aria-label="Go to overview for this date"
                  >
                    <mat-icon>north</mat-icon>
                  </button>
                </div>
              </div>

              <div class="heat-trace-empty" *ngIf="filteredSearchResults.length === 0" aria-hidden="true"></div>
            </div>

            <div class="heat-trace-nav__row" *ngIf="!isSearchMode && filteredWeekGroups.length > 0; else noTestsTemplate">
              <button
                type="button"
                class="heat-trace-nav__arrow"
                (click)="goToPreviousWeek()"
                [disabled]="!canGoPrevious"
                aria-label="Show previous heat trace week"
              >
                &lt;
              </button>

              <div class="grid-container heat-trace-nav__columns">
                <div *ngFor="let weekGroup of filteredWeekGroups">
                  <div class="col-container">
                    <app-week-header [data]="withoutTests(weekGroup)" [isCurrentWeek]="isCurrentWeek(weekGroup)" />
                    <div *ngFor="let testItem of weekGroup.tests">
                      <a [routerLink]="['/heat-trace', testItem.tagNumber]" aria-label="Heat trace">
                        <app-pipe-test [testData]="testItem" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="button"
                class="heat-trace-nav__arrow"
                (click)="goToNextWeek()"
                [disabled]="!canGoNext"
                aria-label="Show next heat trace week"
              >
                &gt;
              </button>
            </div>

            <ng-template #noTestsTemplate>
              <div class="heat-trace-empty" *ngIf="!isSearchMode" aria-hidden="true"></div>
            </ng-template>
          </div>

          <div class="heat-trace-silkscreen" *ngIf="testsLoading$ | async" aria-live="polite" aria-busy="true"></div>
        </div>
      </ng-template>
    </mat-card-content>
  </mat-card>
</div>
